/**
 * What an inventory operation answers with.
 *
 * Every operation returns a result rather than throwing. Inventory moves happen while a raid is
 * running, on a host stepping four players, and an exception there costs a frame and a stack trace
 * to say what a field could have said. A failure carries the numbers that explain it, so a screen
 * that does not exist yet needs no second lookup to say why the bag would not take the plate.
 */

import type { AffixId, AffixSlot } from '@/sim/items/affixContract';
import type { ItemId } from '@/sim/items/itemContract';
import type { SlotId } from '@/sim/inventory/slotContract';

/** Which of a character's two containers an operation is talking about. */
export type ContainerId = 'carried' | 'stash';

export type InventoryFailure =
  /** A string from a save or the wire that no longer names an item. */
  | { readonly reason: 'unknownItem'; readonly defId: string }
  /** A string from a save that no longer names an equipment slot. */
  | { readonly reason: 'unknownSlot'; readonly slot: string }
  /** Zero, negative, fractional, or more units than the stack holds. */
  | { readonly reason: 'invalidCount'; readonly count: number }
  | {
      readonly reason: 'overCapacity';
      readonly container: ContainerId;
      readonly weightKg: number;
      readonly capacityKg: number;
    }
  | { readonly reason: 'noSuchStack'; readonly container: ContainerId; readonly index: number }
  /** Splitting or merging something that only ever exists one at a time. */
  | { readonly reason: 'notStackable'; readonly defId: ItemId }
  | { readonly reason: 'stackMismatch'; readonly from: ItemId; readonly to: ItemId }
  | { readonly reason: 'sameStack'; readonly index: number }
  | { readonly reason: 'sameContainer'; readonly container: ContainerId }
  /** The item does not go there. A helmet is not a sidearm. */
  | { readonly reason: 'wrongSlot'; readonly slot: SlotId; readonly defId: ItemId }
  | { readonly reason: 'notEquippable'; readonly defId: ItemId }
  | { readonly reason: 'emptySlot'; readonly slot: SlotId }
  /** A save written by a version this build does not know how to read. */
  /** A roll from a save naming an affix or a rarity this build no longer has. */
  | { readonly reason: 'unknownAffix'; readonly affixId: string }
  | { readonly reason: 'unknownRarity'; readonly rarity: string }
  /** A prefix used as a suffix, or the reverse. Half a tradeoff is not a tradeoff. */
  | { readonly reason: 'affixSlotMismatch'; readonly affixId: AffixId; readonly slot: AffixSlot }
  /** A roll on something that is not the gun it claims to be, so there is nothing to modify. */
  | { readonly reason: 'rollOnNonWeapon'; readonly defId: ItemId }
  | { readonly reason: 'versionMismatch'; readonly found: number; readonly expected: number };

export type InventoryResult<T> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly failure: InventoryFailure };

export function succeed<T>(value: T): InventoryResult<T> {
  return { ok: true, value };
}

export function fail<T>(failure: InventoryFailure): InventoryResult<T> {
  return { ok: false, failure };
}
